<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="submitBtn"
    :secondary-cta="resetButton"
    @close="onClose"
    @reset="onReset"
    @submit.prevent="onSubmit"
  >
    <template v-if="formState.view" #header-info>
      <span v-if="formState.view">
        <PBtn class="q-pa-none" flat dense @click="openFilesDrawer">
          <DocumentIcon color="primary" class="icon--sm q-mr-xxs" />
          <span class="text-xs">
            Files ({{ formData?.file?.length || 0 }})
          </span>
        </PBtn>
      </span>
      <span>
        <PBadge
          :type="formData.status"
          :label="formData.status"
          class="text-capitalize q-mx-md"
        />
        <PTooltip
          v-if="formData.status === 'Cancelled' && formData?.reason?.length"
        >
          {{ formData.reason }}
        </PTooltip>
      </span>
    </template>
    <PodCustomerDetails v-if="formState.view" :user-details="formData" />
    <div v-else class="border-none q-ma-none q-pa-none">
      <span>Status:</span>
      <PBadge
        class="text-capitalize q-ml-xs"
        :label="formData.status"
        :type="formData.status"
      />
    </div>
    <q-separator v-if="!formState.view" spaced="xl" />
    <div v-if="!formState.view" class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div v-if="!formState.preview" class="col">
          <PSelectInput
            v-model="formData.invoice"
            :loading="validatingNumber"
            :options="invoiceOptions"
            :rules="[validateNumber]"
            :validations="{
              label: 'Invoice',
            }"
            input-class="ellipsis"
            label="Select an Invoice"
            option-label="number"
            option-value="_id"
            placeholder="Select invoice"
            required
            :emit-value="false"
            :map-options="false"
            @virtual-scroll="invoiceFilter.onScroll"
            @filter="invoiceFilter.onFilter"
            @filter-abort="invoiceFilter.onFilterAbort"
            @update:model-value="populateFormData"
          />
        </div>
        <div class="col">
          <PInput
            v-model.trim="formData.number"
            :disable="formState.preview"
            label="POD No."
            placeholder="Enter value"
            required
          />
        </div>
        <div class="col">
          <PVDateInput
            v-model="formData.date"
            :disable="formState.preview"
            :max-date="currentDate"
            label="POD Stamp Date"
            placeholder="DD-MM-YYYY"
            required
          />
        </div>
        <div class="col">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            required
            disable
          />
        </div>
        <div v-if="formState.preview" class="col-12">
          <p class="q-mb-xs">POD File:</p>
          <PMap v-if="formData?.pod_file?.length" :files="formData?.pod_file" />
        </div>
      </div>
    </div>
    <q-separator
      v-if="!formState.preview && !formState.view"
      class="q-mb-xl q-mt-sm"
    />
    <div
      v-if="!formState.preview && !formState.view"
      class="border-none q-ma-none q-pa-none"
    >
      <PField
        v-model="formData.status_cd"
        label="Are there any returned / damaged products?"
      >
        <template #control>
          <POptionGroup
            v-model="formData.status_cd"
            :options="[
              {
                description: 'All well. No returns',
                label: 'No Return',
                value: 'received',
              },
              {
                description: 'Partially Returned case',
                label: 'Yes, Partial Return',
                value: 'returns',
              },
              {
                description: 'Fully Returned/Rejected/CN case',
                label: 'Full Return',
                value: 'rejected',
              },
            ]"
            @update:model-value="onUpdateStatus"
          />
        </template>
      </PField>
    </div>
    <q-separator
      v-if="isReturned && formData.invc_id"
      class="q-mb-xl q-mt-sm"
    />
    <div v-if="isReturned && formData.invc_id" class="overflow-hidden q-mt-lg">
      <legend class="q-mb-sm text-neutral text-weight-medium">
        Returned/Rejected Items ({{ formData.lineitems.length }})
      </legend>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :pagination="pagination"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :columns="productListColumns"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        table-header-class="text-neutral-70 bg-neutral-10"
        title-class="text-neutral text-md"
        dense
        flat
        wrap-cells
      >
        <template #body-cell="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable
              :model-value="props.value"
              :type="props.col.type"
              disable
            />
          </q-td>
        </template>

        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <ItemNameCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-rate="props">
          <q-td :key="props.col.name" :props="props">
            <ItemRateCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-amount="props">
          <q-td :key="props.col.name" :props="props">
            <ItemAmountCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-qty="props">
          <q-td :key="props.col.name" :props="props">
            <ItemQuantityCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-action="props">
          <q-td :props="props">
            <div class="flex no-wrap justify-center">
              <PBtn
                :disable="formState.preview"
                color="primary"
                dense
                flat
                fluid
                rounded
                title="Edit Item"
                @click="
                  editItem({
                    item: props.row,
                    index: props.rowIndex,
                    entityType: 'Proof of Delivery',
                  })
                "
              >
                <EditIcon class="icon--sm" />
              </PBtn>
              <PBtn
                :disable="formState.preview"
                dense
                flat
                rounded
                title="Delete Item"
              >
                <RemoveIcon
                  class="icon--sm"
                  @click="removeProduct(props.rowIndex)"
                />
              </PBtn>
            </div>
          </q-td>
        </template>
      </PTable>
      <p v-else class="q-my-xl text-neutral-50">
        <span class="block q-mb-xs">No Items</span>
        <span class="text-error-60">
          <q-icon class="icon--sm" name="sym_r_error_outline" />
          At least one item is required.
        </span>
      </p>
      <div v-if="!formState.preview && !formState.view" class="flex">
        <PBtn
          class="q-mt-md q-px-xs"
          dense
          outline
          @click="
            addItem({
              entityType: 'Proof of Delivery',
              items: invoiceLineItems,
            })
          "
        >
          <AddIcon class="icon--sm q-mr-sm" />
          <span>Add return items to POD</span>
        </PBtn>
      </div>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div v-if="formState.view" class="col-6">
          <div>
            <span class="text-neutral-80">Remarks </span>
          </div>
          <div>
            <span> {{ formData?.remarks || "" }} </span>
          </div>
        </div>
        <div v-else :class="[formState.preview ? 'col-12' : 'col-6']">
          <PInput
            v-model="formData.remarks"
            :disable="formState.preview"
            label="Remarks (if any)"
            placeholder="Enter remarks"
            type="textarea"
            autogrow
          />
        </div>
        <div v-if="isReturned && !formState.preview" class="col-6">
          <div class="row q-col-gutter-sm">
            <div class="col-6 ellipsis">
              <p
                class="text-neutral q-mb-none ellipsis"
                title="Total Returned Amount"
              >
                Total Returned Amount
              </p>
              <span class="text-xs text-neutral-70">Excludes Tax</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount
                      ? getTotalAmountWithoutTax()
                      : 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6">
              <span class="text-neutral"> Total Returned Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div v-if="formData.tcs?.code && !formState.view" class="row q-mt-lg">
            <div class="col-6 ellipsis">
              <span class="text-neutral" title="TCS">
                TCS ({{ formData.tcs.percentage }}%)
                <sup>*</sup>
              </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount ? formData.tcs.amount : 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-mt-lg">
            <div class="col-6 ellipsis">
              <p
                class="text-neutral q-mb-none ellipsis"
                title="Grand Total Returned"
              >
                Grand Total Returned
              </p>
              <span class="text-xs text-neutral-70">Includes Tax</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-md" />
    <div v-if="formState.view">
      <AssociatedVouchers :id="formData?._id" :type="'pod'" />
    </div>
    <div
      v-if="!formState.preview && !formState.view"
      class="border-none q-ma-none q-pa-none"
    >
      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PFile
            v-model="file"
            label="Attach Proof of Document (POD)"
            help="Allowed file types are PDFs, images, excel, csv"
            :required="formData.status_cd !== 'rejected'"
          />
          <PFile
            v-if="file"
            v-model="file"
            class="q-mt-xxs"
            variant="re-upload"
          />
        </div>
        <div class="col-12 flex justify-between">
          <span class="text-weight-bold"> {{ FORM_INFO }}</span>
          <q-checkbox
            v-model="requestDebitNote"
            class="q-ml-auto"
            label="Request a Debit Note after submitting POD"
            size="sm"
            dense
            left-label
          />
        </div>
      </div>
    </div>
    <div v-if="formState.preview">
      <p>Associated Credit Notes ({{ formData?.credit_notes?.length }})</p>

      <q-separator v-if="formData?.credit_notes?.length" />

      <PTable
        v-if="formData?.credit_notes?.length"
        :columns="creditNotesColumns"
        :rows="formData.credit_notes"
        expandable-rows
        hide-bottom
        dense
      >
        <template #row="props">
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            <span v-if="col.name === 'number'">
              <PBtn
                class="text-xs"
                dense
                flat
                fluid
                text
                @click="previewCreditNote(props.row)"
              >
                {{ col.value || "View Details" }}
              </PBtn>
            </span>

            <PBadge
              v-else-if="col.name === 'status'"
              :label="col.value"
              :type="col.value"
            />

            <PBadge
              v-else-if="col.name === 'stage'"
              :label="col.value"
              :type="col.value"
            />

            <PPopover v-else-if="col.name === 'remarks'" :value="col.value" />

            <span v-else>{{ col.value }}</span>
          </q-td>
        </template>
        <template #expanded-row="props">
          <q-td></q-td>
          <q-td :colspan="productsTableColumns.length - 1">
            <div class="flex">
              <PTabs
                :model-value="getActiveTab(props.rowIndex)"
                align="left"
                dense
                shrink
                @update:model-value="
                  (activeTab) => setActiveTab(activeTab, props.rowIndex)
                "
              >
                <PTab
                  v-for="({ disable, name, label }, index) in tabs"
                  :key="index"
                  :class="{ 'text-neutral-70': activeTab !== name }"
                  :disable="disable"
                  :name="name"
                  :label="label"
                  class="col"
                />
              </PTabs>
            </div>
            <q-tab-panels
              :model-value="getActiveTab(props.rowIndex)"
              class="transparent"
              animated
            >
              <q-tab-panel class="q-pa-none" name="products">
                <PMarkupTable
                  :columns="productsTableColumns"
                  :rows="props.row.lineitems"
                />
              </q-tab-panel>
            </q-tab-panels>
          </q-td>
        </template>
      </PTable>
    </div>
  </PForm>
</template>

<script setup>
import { date } from "quasar";

import AddIcon from "assets/icons/add.svg";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";
import DocumentIcon from "assets/icons/document.svg";

import podFormModel from "src/modules/purchase-order/models/podForm";
import PodCustomerDetails from "src/modules/proof-of-deliveries/components/PodCustomerDetails.vue";

import { numberFormat } from "assets/scripts/utilities/helpers";
import { FORM_INFO } from "assets/scripts/utilities/constants";
import { notify } from "assets/scripts/utilities/api-clients";

const { viewFile } = useFile();
const { toggleAssociatedDataDrawer } = usePaymentRequestStore();
const status = useColor();
const { formatDate } = date;

const podStatusOptions = ref([
  {
    labelClass: "text-success-80",
    label: "Received",
    value: "received",
  },
  {
    labelClass: "text-warning-70",
    label: "Received with Returns",
    value: "returns",
  },
  {
    labelClass: "text-error-70",
    label: "Rejected",
    value: "rejected",
  },
]);

const currentDate = new Date();

const validatingNumber = ref(false);
const isActivePOD = ref(false);

const onUpdateStatus = (status_cd) => {
  unref(formData).status = unref(podStatusOptions).find(
    (status) => status.value === status_cd,
  ).label;

  if (status_cd === "received") {
    unref(formData).lineitems = [];
  }

  calculateTotalTax();
};

const isReturned = computed(() => {
  return ["returns", "rejected"].includes(unref(formData).status_cd);
});

const pForm = ref(null);

const { formState, pod } = storeToRefs(usePodStore());
const { purchaseOrder } = storeToRefs(usePurchaseOrderStore());
const { getPods, postPod, putPod, togglePodForm, isDuplicate } = usePodStore();
const { removeQueryParams } = useRouterUtilities();

const formHeading = computed(() => {
  let heading = "POD";

  if (unref(formState).view) {
    return unref(formData).number || "-";
  }

  if (unref(formState).preview) {
    return `${heading} for Invoice: ${unref(formData).invc_number}`;
  }

  if (unref(formState).edit) {
    return `Edit ${heading}`;
  }

  return `Add ${heading}`;
});

let invoiceFilter = ref({});

watchEffect(() => {
  invoiceFilter.value = useFilter({
    entity: "invoice",
    options: {
      params: {
        po_id: unref(purchaseOrder)?._id,
        stages: "pod_pending,pod_rejected",
      },
    },
  });
});

const invoiceOptions = computed(() => {
  return (
    unref(invoiceFilter.value?.entities)?.filter((invoice) => {
      return invoice.status_cd !== "cancelled" && invoice.number;
    }) || []
  );
});

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: podFormModel(),
  entity: pod,
  formRef: pForm,
  isForm: formState,
  post: postPod,
  put: putPod,
  toggleForm: togglePodForm,
});

const onClose = () => {
  removeQueryParams();

  closeForm();
};

const {
  pagination,
  addItem,
  editItem,
  removeProduct,
  getTotalAmountWithoutTax,
  calculateTotalTax,
} = useProductList({
  formData,
  purchaseOrder,
});

const productListColumns = computed(() => {
  const columns = [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: `Qty ${unref(formState).view ? "" : "Returned"}`,
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
    {
      align: "center",
      classes: "sticky--right",
      headerClasses: "sticky--right",
      name: "action",
      label: "Action",
      hidden: unref(formState).preview || unref(formState).view,
    },
  ];

  return columns;
});

const invoiceLineItems = ref([]);

const populateFormData = (invoice) => {
  const { _id, number, po_id, vehicle_number, lineitems = [] } = invoice;

  formData.value = {
    ...podFormModel()(),
    invc_id: _id,
    invc_number: number,
    number: number,
    po_id: po_id?._id || po_id,
    vehicle_number: vehicle_number || "",
  };

  unref(formData).invoice = invoice;

  file.value = null;

  invoiceLineItems.value = lineitems;
};

const onReset = () => {
  resetForm();
};

const requestDebitNote = ref(false);

watch(isReturned, (returned) => {
  requestDebitNote.value = returned;
});

const { getCounts, getPurchaseOrderFiles } = usePurchaseOrderStore();
const { file, uploadFile } = useFile(formData);
const { getCreditNotes } = useCreditNoteStore();
const { toggleDebitNoteRequestForm } = useDebitNoteStore();

const onSubmit = async () => {
  const submission = await submitForm(false);

  isLoading.value = true;

  if (unref(file)) {
    await uploadFile({
      ...submission?.data?.data,
      type: "Proof of Delivery",
      showMessage: false,
    });
  }

  await Promise.all([
    getCounts(),
    getPods(),
    getCreditNotes(),
    getPurchaseOrderFiles(),
  ]);

  isLoading.value = false;

  if (unref(requestDebitNote)) {
    toggleDebitNoteRequestForm({ open: true });
  } else {
    closeForm(true);
  }

  notify({
    type: "positive",
    message: submission?.data?.message,
  });
};

const podHasLineItems = computed(() => {
  return (
    unref(formData).status_cd === "received" ||
    unref(formData)?.lineitems?.length > 0
  );
});

const submitBtn = computed(() => {
  if (unref(formState).preview || unref(formState).view) {
    return {};
  }

  return {
    loading: unref(isLoading),
    disable:
      unref(validatingNumber) || unref(isActivePOD) || !unref(podHasLineItems),
  };
});

const resetButton = computed(() => {
  if (unref(formState).preview || unref(formState).view) {
    return {};
  }

  return undefined;
});

const validateNumber = async (invoice) => {
  try {
    validatingNumber.value = true;

    isActivePOD.value = false;

    const payload = {
      invc_id: invoice._id,
      invc_number: invoice.number,
      po_id: invoice.po_id,
    };

    await isDuplicate(payload);

    validatingNumber.value = false;

    return true;
  } catch (error) {
    validatingNumber.value = false;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      isActivePOD.value = true;

      return `There is active POD against this invoice ${invoice.number}. Please cancel the active POD from POD Tab before adding new one `;
    }
  }
};

const creditNotesColumns = ref([
  {
    align: "left",
    name: "number",
    label: "Number",
    field: "number",
  },
  {
    align: "left",
    name: "date",
    label: "Date",
    field: "date",
    format: (date, cn) =>
      cn.stage === "Completed" ? formatDate(date, "DD MMM YYYY") || "-" : "-",
  },
  {
    align: "left",
    name: "status",
    label: "Status",
    field: "status",
  },
  {
    align: "left",
    name: "stage",
    label: "Stage",
    field: "stage",
  },
  {
    align: "right",
    name: "total_amount",
    label: "Total",
    field: "total_amount",
    format: (totalAmount) => {
      return numberFormat({
        number: totalAmount,
        style: "currency",
      });
    },
  },
]);

const tabs = ref([
  {
    disable: false,
    name: "products",
    label: "Products",
  },
]);

const activeTab = ref("products");

const { setActiveTab, getActiveTab } = useTabs(activeTab);

const productsTableColumns = ref([
  {
    label: "No.",
    field: "rowIndex",
    format: (value) => {
      return value + 1;
    },
  },
  {
    label: "Name",
    field: "name",
  },
  {
    label: "HSN/SAC",
    field: "hsn",
  },
  {
    label: "GST (%)",
    field: "gst",
  },
  {
    label: "CESS (%)",
    field: "cess",
  },
  {
    label: "Qty",
    field: "qty",
    format: (value, row) => `${value} ${row.unit || "Unit"}`,
  },
  {
    align: "right",
    label: "Rate (Excl. Tax)",
    field: "rate",
    format: (value) => numberFormat({ number: value, style: "currency" }),
  },
  {
    align: "right",
    label: "Rate (Incl. Tax)",
    field: "rate_wt",
    format: (value) =>
      value ? numberFormat({ number: value, style: "currency" }) : "-",
  },
  {
    align: "right",
    label: "Amount (Excl. Tax)",
    field: "amount",
    format: (value) => numberFormat({ number: value, style: "currency" }),
  },
  {
    align: "right",
    label: "Amount (Incl. Tax)",
    field: "amount_wt",
    format: (value) =>
      value ? numberFormat({ number: value, style: "currency" }) : "-",
  },
]);

const { setCreditNote, toggleCreditNoteForm } = useCreditNoteStore();

const previewCreditNote = (creditNote) => {
  setCreditNote(creditNote);

  toggleCreditNoteForm({
    level: 1,
    edit: true,
    open: true,
    preview: true,
  });
};

const filesColumns = computed(() => {
  return [
    {
      align: "left",
      label: "Date",
      field: "created_date",
      format: (value) => {
        return formatDate(value, "DD MMM YYYY");
      },
    },
    {
      align: "left",
      label: "Name",
      field: "file_name",
      cellClass: "text-primary text-weight-medium underline cursor-pointer",
      onClick: viewFile,
    },
    {
      align: "center",
      label: "Status",
      field: "status",
      format: (value) =>
        `<span class="bg-${status(value).color} text-${
          status(value).textColor
        } q-px-sm q-py-xxs rounded" title="Status">${value}</span>`,
      cellClass: "q-px-sm q-py-xxs rounded",
    },
  ];
});

const openFilesDrawer = () => {
  toggleAssociatedDataDrawer({
    edit: false,
    level: 0,
    open: true,
    view: true,
    props: {
      columns: unref(filesColumns),
      rows: formData.value?.file,
      heading: "Associated Files",
      type: "file",
    },
  });
};
</script>
